import { useRoleLabels } from "../lib/RoleLabelsContext";
import { setExerciseTimeout, clearExerciseTimeout } from "../exercises/pause-clock";
import { ReadingPreviewNotice, useReadingPreview } from "./ReadingPreview";
import { useLearningDisplayItem } from "../exercises/LearningDisplayBoundary";
import { RecommendationPlanFields, emptyRecommendationPlan, recommendationPlanInput } from "./RecommendationPlanFields";
import { useRecommendationRunStarter } from "../exercises/recommendation-context";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { BlitzlesenExerciseConfig, BlitzlesenRunSettings } from "@calega/content";
import { blitzlesenSyllableCount, blitzlesenSyllables, type BlitzlesenRun } from "@calega/exercise-engine";
import type { Account } from "../lib/types";
import { api } from "../lib/api";
import { useRealtime } from "../realtime/RealtimeContext";
import { ExerciseMenu } from "./ExerciseMenu";
import { ExerciseWaitingCard } from "./ExerciseWaitingCard";
import { editableInteger, type EditableInteger } from "./editable-integer";
import { abortExerciseRun, startAdaptiveAssignmentRun, startIndividualAssignmentRun, submitExerciseAnswer } from "../exercises/learning-api";
import { CompletedRunActions } from "./CompletedRunActions";
import { CompletedRunHistory } from "./CompletedRunHistory";
import { RunCompletion, useRunCompletion } from "./RunCompletion";
import {
  isBlitzlesenWordVisible,
  newestFirst,
  scheduleBlitzlesenPresentation,
  shouldEnterBlitzlesenRating,
} from "./blitzlesen-presentation";

type Rating = "correct" | "uncertain" | "incorrect";
type Phase = "ready" | "showing" | "rating" | "review" | "complete";

interface RatingEvent {
  sequence: number;
  wordId: string;
  rating: Rating;
}

interface PresentationEvent {
  wordId: string;
  displayDurationMs: number;
  displayStartsAt: number;
}

interface BlitzlesenState {
  runId: string;
  run: BlitzlesenRun;
  index: number;
  phase: Phase;
  displayStartsAt: number;
  displayUntil: number;
  liveSettings: Pick<BlitzlesenRunSettings, "displayDurationMs" | "syllableColors">;
  presentations: PresentationEvent[];
  ratings: RatingEvent[];
}

interface AdaptiveLearner {
  id: string;
  displayName: string;
}

function latestRatings(events: RatingEvent[]) {
  const latest = new Map<string, Rating>();
  for (const event of events) latest.set(event.wordId, event.rating);
  return latest;
}

function clampDuration(value: number) {
  return Math.max(50, Math.min(9000, Number.isFinite(value) ? value : 50));
}

export function formatBlitzlesenCopyList(entries: Array<{ text: string; displayDurationMs: number; rating?: Rating }>) {
  return [
    "Blitzlesen:",
    ...entries.map(({ text, displayDurationMs, rating }) => {
      const marker = rating === "correct" ? "✅ " : rating === "uncertain" ? "🟡 " : rating === "incorrect" ? "❌ " : "";
      return `- ${marker}${text} (${displayDurationMs} ms)`;
    }),
  ].join("\n");
}

function initialSyllableSelection(config: BlitzlesenExerciseConfig): Array<1 | 2 | 3 | 4> {
  return config.syllableSelectionMode === "single"
    ? [config.selectableSyllableCounts[0]!]
    : [...config.selectableSyllableCounts];
}

function initialMaterialBank(config: BlitzlesenExerciseConfig) {
  return config.materialBanks.find(({ id }) => id === config.defaultMaterialBankId) ?? config.materialBanks[0] ?? null;
}

function materialEntryCount(bank: BlitzlesenExerciseConfig["materialBanks"][number] | null, groupIds: readonly string[]) {
  return bank?.groups.filter(({ id }) => groupIds.includes(id)).reduce((sum, group) => sum + group.entries.length, 0) ?? 0;
}

function initialBlitzlesenCount(config: BlitzlesenExerciseConfig) {
  const bank = initialMaterialBank(config);
  return Math.min(25, bank ? materialEntryCount(bank, bank.defaultGroupIds) : config.words.length);
}

function initialSyllableColors(config: BlitzlesenExerciseConfig) {
  const bank = initialMaterialBank(config);
  if (bank) return config.syllableColorsDefault && bank.groups.some(({ id, syllableColors }) => bank.defaultGroupIds.includes(id) && syllableColors);
  return config.syllableColorsDefault && initialSyllableSelection(config).some((count) => config.syllableColorCounts.includes(count));
}

export function Blitzlesen({
  account,
  config,
  pageId,
  adaptiveAssignmentId,
  individualAssignmentId,
}: {
  account: Account;
  config: BlitzlesenExerciseConfig;
  pageId: string;
  adaptiveAssignmentId?: string;
  individualAssignmentId?: string;
}) {
  const labels = useRoleLabels();
  const startExerciseRun = useRecommendationRunStarter();
  const completion = useRunCompletion();
  const realtime = useRealtime();
  const facilitator = account.roles.some((role) => role === "ADMIN" || role === "ORGANIZATION" || role === "THERAPIST");
  const syncFollower = Boolean(realtime.session && !facilitator);
  const canControl = !realtime.session || facilitator;
  const syncControlReady = !realtime.session || realtime.synchronized;
  const showTherapistList = Boolean(realtime.session && facilitator);
  const canCreateAdaptive = !adaptiveAssignmentId
    && !individualAssignmentId
    && Boolean(account.modules?.adaptiveAssignments)
    && account.roles.some((role) => role === "ORGANIZATION" || role === "THERAPIST");
  const [selectedSyllables, setSelectedSyllables] = useState<Array<1 | 2 | 3 | 4>>(() => initialSyllableSelection(config));
  const [selectedMaterialBankId, setSelectedMaterialBankId] = useState(() => initialMaterialBank(config)?.id ?? "");
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>(() => [...(initialMaterialBank(config)?.defaultGroupIds ?? [])]);
  const [wordCount, setWordCount] = useState(() => initialBlitzlesenCount(config));
  const [displayDurationMs, setDisplayDurationMs] = useState<EditableInteger>(config.defaultDisplayDurationMs);
  const [liveDisplayDurationDraft, setLiveDisplayDurationDraft] = useState<EditableInteger | null>(null);
  const [syllableColors, setSyllableColors] = useState(() => initialSyllableColors(config));
  const [adaptiveComposerOpen, setAdaptiveComposerOpen] = useState(false);
  const [adaptiveLearners, setAdaptiveLearners] = useState<AdaptiveLearner[]>([]);
  const [adaptiveLearnersLoaded, setAdaptiveLearnersLoaded] = useState(false);
  const [adaptiveLearnerId, setAdaptiveLearnerId] = useState(account.actingAs?.id ?? "");
  const [adaptiveTitle, setAdaptiveTitle] = useState("Blitzlesen für zuhause");
  const [adaptiveMessage, setAdaptiveMessage] = useState("");
  const [recommendationPlan, setRecommendationPlan] = useState(emptyRecommendationPlan);
  const [adaptiveProfile, setAdaptiveProfile] = useState<"gentle" | "balanced" | "focused">("balanced");
  const [exercise, setExercise] = useState<BlitzlesenState | null>(null);
  const [dismissedActivation, setDismissedActivation] = useState<string | null>(null);
  const displayRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const closeFullscreenRef = useRef<HTMLButtonElement>(null);
  const fullscreen = realtime.state.blitzlesenFullscreen;
  const fullscreenMatches = Boolean(exercise && fullscreen?.pageId === pageId && fullscreen.runId === exercise.runId
    && (exercise.phase === "ready" || exercise.phase === "showing" || exercise.phase === "rating"));
  const fullscreenActive = Boolean(syncFollower && realtime.connected && fullscreenMatches
    && fullscreen?.activationId !== dismissedActivation && completion.status.phase === "idle");
  useLearningDisplayItem(exercise?.runId, exercise?.index ?? 0, Boolean(exercise && exercise.phase !== "complete"));
  useLayoutEffect(() => { window.dispatchEvent(new CustomEvent("calepia:blitz-phase", { detail: exercise?.phase ?? "" })); }, [exercise?.phase]);
  const [message, setMessage] = useState("");
  const [copyMessage, setCopyMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [, setClockTick] = useState(0);
  const sequence = useRef(0);

  function closeFullscreen() {
    if (!exercise || !fullscreen) return;
    setDismissedActivation(fullscreen.activationId);
    realtime.publishBlitzlesenFullscreen({ pageId, runId: exercise.runId, enabled: false });
  }
  const closeFullscreenAction = useRef(closeFullscreen);
  closeFullscreenAction.current = closeFullscreen;

  useLayoutEffect(() => {
    if (!fullscreenActive || !displayRef.current || !measureRef.current) return;
    const display = displayRef.current;
    const measure = measureRef.current;
    const fit = () => {
      const style = getComputedStyle(display);
      const width = display.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      const height = display.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
      const measured = measure.getBoundingClientRect();
      if (!measured.width || !measured.height) return;
      const size = Math.min(160, display.clientWidth * .16, display.clientHeight * .22,
        160 * width / measured.width, 160 * height / measured.height);
      display.style.setProperty("--blitz-fullscreen-font-size", `${Math.max(1, Math.floor(size))}px`);
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(display);
    observer.observe(measure);
    return () => { observer.disconnect(); display.style.removeProperty("--blitz-fullscreen-font-size"); };
  }, [fullscreenActive, exercise?.run.words[exercise.index]?.text]);

  useEffect(() => {
    if (!fullscreenActive || !displayRef.current) return;
    const display = displayRef.current;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const background: Array<{ element: HTMLElement; inert: boolean }> = [];
    let foreground: HTMLElement = display;
    while (foreground.parentElement && foreground !== document.body) {
      for (const sibling of foreground.parentElement.children) {
        if (!(sibling instanceof HTMLElement) || sibling === foreground
          || sibling.matches(".print-help-toolbar, .session-idle-warning")) continue;
        background.push({ element: sibling, inert: sibling.inert });
        sibling.inert = true;
      }
      foreground = foreground.parentElement;
    }
    closeFullscreenRef.current?.focus({ preventScroll: true });
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") { event.preventDefault(); closeFullscreenAction.current(); }
    };
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("keydown", escape);
      document.body.style.overflow = previousOverflow;
      for (const { element, inert } of background) element.inert = inert;
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, [fullscreenActive]);

  const syllableCounts = useMemo(() => {
    const counts = new Map<number, number>([[1, 0], [2, 0], [3, 0], [4, 0]]);
    for (const word of config.words) {
      const count = blitzlesenSyllableCount(word);
      counts.set(count, (counts.get(count) ?? 0) + 1);
    }
    return counts;
  }, [config.words]);
  const selectedMaterialBank = useMemo(
    () => config.materialBanks.find(({ id }) => id === selectedMaterialBankId) ?? null,
    [config.materialBanks, selectedMaterialBankId],
  );
  const sourceAvailableWords = useMemo(
    () => selectedMaterialBank
      ? materialEntryCount(selectedMaterialBank, selectedGroupIds)
      : config.words.filter((word) => selectedSyllables.includes(blitzlesenSyllableCount(word))).length,
    [config.words, selectedGroupIds, selectedMaterialBank, selectedSyllables],
  );
  const previewSettings: BlitzlesenRunSettings = {
    syllableCounts: selectedMaterialBank ? [] : selectedSyllables,
    ...(selectedMaterialBank ? { materialBankId: selectedMaterialBank.id } : {}),
    groupIds: selectedMaterialBank ? selectedGroupIds : [],
    wordCount: Math.max(1, Math.min(wordCount, sourceAvailableWords)),
    displayDurationMs: displayDurationMs === "" ? config.defaultDisplayDurationMs : displayDurationMs,
    syllableColors,
  };
  const preview = useReadingPreview({ ...(adaptiveAssignmentId ? { adaptiveAssignmentId } : individualAssignmentId ? { individualAssignmentId } : { contentPageId: pageId }), settings: previewSettings }, account.actingAs?.id ?? account.id, !exercise && canControl);
  const availableWords = preview.preview?.eligible ?? sourceAvailableWords;
  const ratings = useMemo(() => latestRatings(exercise?.ratings ?? []), [exercise?.ratings]);
  const colorsAvailableForSelection = selectedMaterialBank
    ? selectedMaterialBank.groups.some(({ id, syllableColors: available }) => selectedGroupIds.includes(id) && available)
    : selectedSyllables.some((count) => config.syllableColorCounts.includes(count));

  function selectMaterialBank(bankId: string) {
    const bank = config.materialBanks.find(({ id }) => id === bankId);
    if (!bank) return;
    setSelectedMaterialBankId(bank.id);
    setSelectedGroupIds([...bank.defaultGroupIds]);
    setWordCount(Math.min(25, materialEntryCount(bank, bank.defaultGroupIds)));
    setSyllableColors(config.syllableColorsDefault && bank.groups.some(({ id, syllableColors: available }) => bank.defaultGroupIds.includes(id) && available));
  }

  function updateMaterialGroup(groupId: string, enabled: boolean) {
    if (!selectedMaterialBank) return;
    const next = selectedMaterialBank.selectionMode === "single"
      ? [groupId]
      : enabled
        ? [...selectedGroupIds, groupId].filter((id, index, ids) => ids.indexOf(id) === index)
        : selectedGroupIds.filter((id) => id !== groupId);
    setSelectedGroupIds(next);
    const count = materialEntryCount(selectedMaterialBank, next);
    setWordCount((current) => Math.min(current, count));
    if (!selectedMaterialBank.groups.some(({ id, syllableColors: available }) => next.includes(id) && available)) setSyllableColors(false);
  }

  useEffect(() => {
    if (!canCreateAdaptive) return;
    let mounted = true;
    setAdaptiveLearnersLoaded(false);
    api<AdaptiveLearner[]>("/auth/acting-as/options")
      .then((learners) => {
        if (!mounted) return;
        setAdaptiveLearners(learners);
        setAdaptiveLearnerId((current) => {
          if (learners.some(({ id }) => id === current)) return current;
          const preferred = account.actingAs?.id ?? "";
          return learners.some(({ id }) => id === preferred) ? preferred : "";
        });
        setAdaptiveLearnersLoaded(true);
      })
      .catch(() => { if (mounted) { setAdaptiveLearners([]); setAdaptiveLearnersLoaded(true); } });
    return () => { mounted = false; };
  }, [account.actingAs?.id, canCreateAdaptive]);

  function updateSyllableSelection(count: 1 | 2 | 3 | 4, enabled: boolean) {
    const next = config.syllableSelectionMode === "single"
      ? [count]
      : enabled
        ? [...selectedSyllables, count].filter((value, index, values) => values.indexOf(value) === index).sort()
        : selectedSyllables.filter((value) => value !== count);
    setSelectedSyllables(next);
    if (!next.some((value) => config.syllableColorCounts.includes(value))) setSyllableColors(false);
  }

  function publish(next: BlitzlesenState) {
    realtime.publishExercise({ exerciseType: "blitzlesen", pageId, state: next });
  }

  function publishReset() {
    realtime.publishExercise({ exerciseType: "blitzlesen", pageId, state: null });
  }

  function changeLiveSettings(change: Partial<BlitzlesenState["liveSettings"]>) {
    if (!exercise || !canControl) return;
    const next = { ...exercise, liveSettings: { ...exercise.liveSettings, ...change } };
    setExercise(next);
    publish(next);
  }

  function commitLiveDisplayDuration() {
    if (liveDisplayDurationDraft === null) return;
    if (liveDisplayDurationDraft !== "") {
      changeLiveSettings({ displayDurationMs: clampDuration(liveDisplayDurationDraft) });
    }
    setLiveDisplayDurationDraft(null);
  }

  useEffect(() => {
    const synced = realtime.state.exercise as { exerciseType?: string; pageId?: string; state?: BlitzlesenState | null } | null;
    if (synced?.exerciseType === "blitzlesen" && synced.pageId === pageId && synced.state === null) {
      setExercise(null);
      return;
    }
    if (synced?.exerciseType === "blitzlesen" && synced.pageId === pageId && synced.state) {
      const legacyState = synced.state as Omit<BlitzlesenState, "displayStartsAt" | "liveSettings" | "presentations"> & Partial<Pick<BlitzlesenState, "displayStartsAt" | "liveSettings" | "presentations">>;
      const liveSettings = legacyState.liveSettings ?? {
        displayDurationMs: legacyState.run.settings.displayDurationMs,
        syllableColors: legacyState.run.settings.syllableColors,
      };
      const displayStartsAt = typeof legacyState.displayStartsAt === "number"
        ? legacyState.displayStartsAt
        : legacyState.phase === "showing"
          ? legacyState.displayUntil - legacyState.run.settings.displayDurationMs
          : 0;
      const presentations = Array.isArray(legacyState.presentations)
        ? legacyState.presentations
        : legacyState.phase === "ready"
          ? []
          : legacyState.run.words.slice(0, legacyState.index + 1).map((word) => ({
              wordId: word.id,
              displayDurationMs: legacyState.run.settings.displayDurationMs,
              displayStartsAt: 0,
            }));
      // Sitzungen aus der vorherigen Version bleiben bis zu ihrem Ende lesbar.
      const synchronizedState: BlitzlesenState = {
        ...legacyState,
        displayStartsAt,
        liveSettings,
        presentations,
      };
      setExercise(synchronizedState);
      sequence.current = Math.max(sequence.current, ...synchronizedState.ratings.map((event) => event.sequence), 0);
    }
  }, [realtime.state.exercise, pageId]);

  useEffect(() => {
    if (!exercise || exercise.phase !== "showing") return;
    const untilStart = Math.max(0, exercise.displayStartsAt - realtime.serverNow());
    const untilEnd = Math.max(0, exercise.displayUntil - realtime.serverNow());
    const startTimer = setExerciseTimeout(() => {
      setClockTick((value) => value + 1);
    }, untilStart + 1);
    const endTimer = setExerciseTimeout(() => {
      setClockTick((value) => value + 1);
      if (shouldEnterBlitzlesenRating(exercise.phase, canControl)) {
        const next = { ...exercise, phase: "rating" as const };
        setExercise(next);
        publish(next);
      }
    }, untilEnd + 10);
    return () => {
      clearExerciseTimeout(startTimer);
      clearExerciseTimeout(endTimer);
    };
  }, [exercise?.phase, exercise?.displayStartsAt, exercise?.displayUntil, exercise?.index, canControl]);

  async function prepareRun(settings: BlitzlesenRunSettings) {
    if (busy) return;
    setBusy(true);
    setMessage("Durchlauf wird vorbereitet …");
    try {
      const result = adaptiveAssignmentId
        ? await startAdaptiveAssignmentRun<{ runId: string; exercise: BlitzlesenRun }>(adaptiveAssignmentId)
        : individualAssignmentId
        ? await startIndividualAssignmentRun<{ runId: string; exercise: BlitzlesenRun }>(individualAssignmentId, settings)
        : await startExerciseRun<{ runId: string; exercise: BlitzlesenRun }>({ contentPageId: pageId, settings });
      sequence.current = 0;
      const next: BlitzlesenState = {
        runId: result.runId,
        run: result.exercise,
        index: 0,
        phase: "ready",
        displayStartsAt: 0,
        displayUntil: 0,
        liveSettings: {
          displayDurationMs: result.exercise.settings.displayDurationMs,
          syllableColors: result.exercise.settings.syllableColors,
        },
        presentations: [],
        ratings: [],
      };
      setExercise(next);
      setMessage("");
      publish(next);
    } finally {
      setBusy(false);
    }
  }

  async function prepareFromSettings() {
    if (displayDurationMs === "") return;
    await prepareRun(selectedMaterialBank ? {
      syllableCounts: [],
      materialBankId: selectedMaterialBank.id,
      groupIds: selectedGroupIds,
      wordCount: Math.min(wordCount, availableWords),
      displayDurationMs,
      syllableColors,
    } : {
      syllableCounts: selectedSyllables,
      groupIds: [],
      wordCount: Math.min(wordCount, availableWords),
      displayDurationMs,
      syllableColors,
    });
  }

  async function createAdaptiveAssignment() {
    if (!canCreateAdaptive || !adaptiveLearnerId || !adaptiveTitle.trim() || (selectedMaterialBank ? selectedGroupIds.length === 0 : selectedSyllables.length === 0) || displayDurationMs === "" || busy) return;
    setBusy(true);
    try {
      await api("/learning/adaptive-assignments", {
        method: "POST",
        body: JSON.stringify({
          learnerId: adaptiveLearnerId,
          contentPageId: pageId,
          title: adaptiveTitle,
          message: adaptiveMessage,
          ...recommendationPlanInput(recommendationPlan),
          exerciseType: "blitzlesen",
          config: selectedMaterialBank ? {
            syllableCounts: [],
            materialBankId: selectedMaterialBank.id,
            groupIds: selectedGroupIds,
            wordCount: Math.min(wordCount, availableWords),
            displayDurationMs,
            syllableColors,
            focusProfile: adaptiveProfile,
          } : {
            syllableCounts: selectedSyllables,
            groupIds: [],
            wordCount: Math.min(wordCount, availableWords),
            displayDurationMs,
            syllableColors,
            focusProfile: adaptiveProfile,
          },
        }),
      });
      setAdaptiveComposerOpen(false);
      setAdaptiveMessage("");
      setRecommendationPlan(emptyRecommendationPlan);
      setMessage("Die adaptive Empfehlung wurde angelegt.");
    } finally { setBusy(false); }
  }

  function beginPresentation() {
    if (!exercise || exercise.phase !== "ready" || !canControl || !syncControlReady || busy) return;
    const presentation = scheduleBlitzlesenPresentation(
      realtime.serverNow(),
      exercise.liveSettings.displayDurationMs,
    );
    const next = {
      ...exercise,
      phase: "showing" as const,
      ...presentation,
      presentations: [...exercise.presentations, {
        wordId: exercise.run.words[exercise.index]!.id,
        displayDurationMs: exercise.liveSettings.displayDurationMs,
        displayStartsAt: presentation.displayStartsAt,
      }],
    };
    if (!realtime.session) setExercise(next);
    publish(next);
  }

  async function abortCurrentRun() {
    if (!exercise || exercise.phase === "complete") return;
    await abortExerciseRun(exercise.runId);
  }

  async function backToSettings() {
    if (!exercise || busy) return;
    setBusy(true);
    try {
      await abortCurrentRun();
      if (exercise.run.settings.materialBankId) {
        setSelectedMaterialBankId(exercise.run.settings.materialBankId);
        setSelectedGroupIds([...exercise.run.settings.groupIds]);
      } else {
        setSelectedSyllables(exercise.run.settings.syllableCounts.filter((count) => config.selectableSyllableCounts.includes(count)));
      }
      setWordCount(exercise.run.settings.wordCount);
      setDisplayDurationMs(exercise.liveSettings.displayDurationMs);
      const runBank = config.materialBanks.find(({ id }) => id === exercise.run.settings.materialBankId);
      const colorsAvailable = runBank
        ? runBank.groups.some(({ id, syllableColors: available }) => exercise.run.settings.groupIds.includes(id) && available)
        : exercise.run.settings.syllableCounts.some((count) => config.syllableColorCounts.includes(count));
      setSyllableColors(exercise.liveSettings.syllableColors && colorsAvailable);
      setExercise(null);
      setMessage("");
      setCopyMessage("");
      publishReset();
    } finally {
      setBusy(false);
    }
  }

  async function remix() {
    if (!exercise || busy) return;
    const settings: BlitzlesenRunSettings = {
      ...exercise.run.settings,
      wordCount: exercise.run.words.length,
      displayDurationMs: exercise.liveSettings.displayDurationMs,
      syllableColors: exercise.liveSettings.syllableColors,
    };
    setBusy(true);
    try {
      await abortCurrentRun();
    } finally {
      setBusy(false);
    }
    await prepareRun(settings);
  }

  async function rate(wordId: string, rating: Rating, advance: boolean) {
    if (!exercise || !syncControlReady || busy || exercise.phase === "review" || exercise.phase === "complete") return;
    setBusy(true);
    try {
      const presentation = exercise.presentations.find((entry) => entry.wordId === wordId);
      const displayDurationMs = presentation?.displayDurationMs ?? exercise.run.settings.displayDurationMs;
      const nextSequence = sequence.current + 1;
      await submitExerciseAnswer(exercise.runId, { type: "blitzlesen", wordId, rating, sequence: nextSequence, displayDurationMs });
      sequence.current = nextSequence;
      const events = [...exercise.ratings, { sequence: nextSequence, wordId, rating }];
      let next: BlitzlesenState = { ...exercise, ratings: events };
      if (advance) {
        if (exercise.index + 1 >= exercise.run.words.length) {
          next = { ...next, phase: "review", displayStartsAt: 0, displayUntil: 0 };
        } else {
          const presentation = scheduleBlitzlesenPresentation(
            realtime.serverNow(),
            exercise.liveSettings.displayDurationMs,
          );
          next = {
            ...next,
            index: exercise.index + 1,
            phase: "showing",
            ...presentation,
            presentations: [...exercise.presentations, {
              wordId: exercise.run.words[exercise.index + 1]!.id,
              displayDurationMs: exercise.liveSettings.displayDurationMs,
              displayStartsAt: presentation.displayStartsAt,
            }],
          };
        }
      }
      if (next.phase === "review") {
        await completion.complete(exercise.runId, () => {
          if (!realtime.session) setExercise(next);
          publish(next);
        });
      } else {
        if (!realtime.session) setExercise(next);
        publish(next);
      }
    } finally {
      setBusy(false);
    }
  }

  async function closeCompletedRun(repeat: boolean) {
    if (!exercise || exercise.phase !== "review") return;
    if (repeat) {
      await prepareRun({
        ...exercise.run.settings,
        wordCount: exercise.run.words.length,
        displayDurationMs: exercise.liveSettings.displayDurationMs,
        syllableColors: exercise.liveSettings.syllableColors,
      });
      return;
    }
    if (exercise.run.settings.materialBankId) {
      setSelectedMaterialBankId(exercise.run.settings.materialBankId);
      setSelectedGroupIds([...exercise.run.settings.groupIds]);
    } else {
      setSelectedSyllables(exercise.run.settings.syllableCounts.filter((count) => config.selectableSyllableCounts.includes(count)));
    }
    setWordCount(exercise.run.settings.wordCount);
    setDisplayDurationMs(exercise.liveSettings.displayDurationMs);
    setSyllableColors(exercise.liveSettings.syllableColors);
    setExercise(null);
    setMessage("");
    setCopyMessage("");
    publishReset();
  }

  function copyResult() {
    if (!exercise) return;
    const presentationByWordId = new Map(exercise.presentations.map((entry) => [entry.wordId, entry]));
    const displayedWords = newestFirst(exercise.run.words.filter((word) => {
      const presentation = presentationByWordId.get(word.id);
      return presentation && presentation.displayStartsAt <= realtime.serverNow();
    }));
    const copyText = formatBlitzlesenCopyList(displayedWords.map((word) => ({
      text: word.text,
      displayDurationMs: presentationByWordId.get(word.id)?.displayDurationMs ?? exercise.run.settings.displayDurationMs,
      rating: ratings.get(word.id),
    })));
    navigator.clipboard.writeText(copyText)
      .then(() => setCopyMessage("Ergebnis wurde kopiert."))
      .catch(() => setCopyMessage("Das Ergebnis konnte nicht kopiert werden."));
  }

  if (syncFollower && !exercise) {
    return <ExerciseWaitingCard className="blitzlesen-exercise" />;
  }

  if (completion.status.phase !== "idle") return <RunCompletion completion={completion} />;

  if (!exercise) {
    return (
      <section className="blitzlesen-exercise">
        <p className="quiz-instructions learning-text">{config.instructions}</p>
        {adaptiveAssignmentId && <div className="alert info"><strong>Adaptives Blitzlesen</strong><p>Der nächste Durchgang wird aus den bisherigen Bewertungen zusammengestellt. Unsichere Einträge werden gezielt wiederholt.</p></div>}
        {!adaptiveAssignmentId && <div className="blitz-settings-grid">
          {config.materialBanks.length > 0 && <fieldset><legend>Material</legend><div className="blitz-choice-row">
            {config.materialBanks.map((bank) => <label className="checkbox-label" key={bank.id}>
              <input type="radio" name={`${pageId}-material`} checked={selectedMaterialBankId === bank.id} onChange={() => selectMaterialBank(bank.id)} />
              {bank.label}
            </label>)}
          </div></fieldset>}
          {selectedMaterialBank ? <fieldset><legend>{selectedMaterialBank.selectionLabel}</legend><div className="blitz-choice-row">
            {selectedMaterialBank.groups.map((group) => <label className="checkbox-label" key={group.id}>
              <input
                type={selectedMaterialBank.selectionMode === "single" ? "radio" : "checkbox"}
                name={selectedMaterialBank.selectionMode === "single" ? `${pageId}-${selectedMaterialBank.id}-gruppe` : undefined}
                checked={selectedGroupIds.includes(group.id)}
                disabled={selectedMaterialBank.selectionMode === "multiple" && selectedGroupIds.length === 1 && selectedGroupIds.includes(group.id)}
                onChange={(event) => updateMaterialGroup(group.id, event.target.checked)}
              />
              {group.label} ({group.entries.length})
            </label>)}
          </div></fieldset> : <fieldset><legend>Silbenanzahl</legend><div className="blitz-choice-row">
            {config.selectableSyllableCounts.map((count) => <label className="checkbox-label" key={count}>
              <input type={config.syllableSelectionMode === "single" ? "radio" : "checkbox"} name={config.syllableSelectionMode === "single" ? `${pageId}-silbenanzahl` : undefined} checked={selectedSyllables.includes(count)} disabled={(syllableCounts.get(count) ?? 0) === 0} onChange={(event) => updateSyllableSelection(count, event.target.checked)} />
              {count === 4 ? "4+-silbig" : `${count}-silbig`} ({syllableCounts.get(count) ?? 0})
            </label>)}
          </div></fieldset>}
          <label>{selectedMaterialBank ? `Anzahl ${selectedMaterialBank.itemLabelPlural}` : "Wortanzahl"}<select value={Math.min(wordCount, availableWords)} onChange={(event) => setWordCount(Number(event.target.value))}>
            {[10, 20, 25, 30, 50, 100, 200].filter((count) => count < availableWords).map((count) => <option value={count} key={count}>{count}</option>)}
            <option value={availableWords}>Alle ({availableWords})</option>
          </select></label>
          <label>Anzeigedauer<input type="number" inputMode="numeric" min={50} max={9000} step={50} value={displayDurationMs} onChange={(event) => setDisplayDurationMs(editableInteger(event.target.value))} onBlur={() => setDisplayDurationMs((value) => value === "" ? 50 : clampDuration(value))} /></label>
          {colorsAvailableForSelection && <label className="checkbox-label"><input type="checkbox" checked={syllableColors} onChange={(event) => setSyllableColors(event.target.checked)} />Silben einfärben</label>}
        </div>}
        <ReadingPreviewNotice preview={preview.preview} error={preview.error} />
        <button className="primary" disabled={busy || !canControl || !preview.preview || preview.preview.eligible === 0 || (!adaptiveAssignmentId && ((selectedMaterialBank ? selectedGroupIds.length === 0 : selectedSyllables.length === 0) || availableWords === 0 || displayDurationMs === "" || displayDurationMs < 50 || displayDurationMs > 9000))} onClick={() => prepareFromSettings().catch((error) => setMessage(error.message))}>{adaptiveAssignmentId ? "Nächsten Durchgang starten" : "Liste vorbereiten"}</button>
        {!adaptiveAssignmentId && canCreateAdaptive && <>
          <button className="secondary adaptive-create-trigger" disabled={busy || (selectedMaterialBank ? selectedGroupIds.length === 0 : selectedSyllables.length === 0) || availableWords === 0 || displayDurationMs === "" || !adaptiveLearnersLoaded || adaptiveLearners.length === 0} onClick={() => setAdaptiveComposerOpen(true)}>Adaptive Empfehlung anlegen</button>
          {adaptiveLearnersLoaded && adaptiveLearners.length === 0 && <p className="muted">{labels.LEARNER.plural}: Keine freigegebenen Konten verfügbar.</p>}
          {adaptiveComposerOpen && <section className="adaptive-assignment-composer" role="dialog" aria-modal="true" aria-labelledby={`${pageId}-adaptive-title`}>
            <div className="adaptive-assignment-composer-heading"><div><p className="eyebrow">Adaptive Empfehlung</p><h2 id={`${pageId}-adaptive-title`}>Empfehlung für zuhause anlegen</h2></div><button type="button" className="secondary small" onClick={() => setAdaptiveComposerOpen(false)}>Schließen</button></div>
            <p className="muted">Die aktuell gewählten Einstellungen werden für jeden neuen Durchgang adaptiv neu zusammengestellt.</p>
            <div className="adaptive-settings-summary"><strong>Gewählte Einstellungen</strong><span>{selectedMaterialBank ? `${selectedMaterialBank.label}: ${selectedMaterialBank.groups.filter(({ id }) => selectedGroupIds.includes(id)).map(({ label }) => label).join(", ")}` : selectedSyllables.map((count) => count === 4 ? "4+-silbig" : `${count}-silbig`).join(", ")} · {Math.min(wordCount, availableWords)} {selectedMaterialBank?.itemLabelPlural ?? "Wörter"} · {displayDurationMs} ms{syllableColors ? " · Silben eingefärbt" : ""}</span></div>
            <label>{labels.LEARNER.singular}<select value={adaptiveLearnerId} onChange={(event) => setAdaptiveLearnerId(event.target.value)}><option value="">{labels.LEARNER.singular} auswählen</option>{adaptiveLearners.map((learner) => <option value={learner.id} key={learner.id}>{learner.displayName}</option>)}</select></label>
            <label>Titel<input value={adaptiveTitle} onChange={(event) => setAdaptiveTitle(event.target.value)} maxLength={160} /></label>
            <label>Anweisung<textarea value={adaptiveMessage} onChange={(event) => setAdaptiveMessage(event.target.value)} maxLength={1_000} placeholder={`Optionale Hinweise (${labels.LEARNER.singular})`} /></label>
        <RecommendationPlanFields value={recommendationPlan} onChange={setRecommendationPlan} />
            <label>Adaptive Gewichtung<select value={adaptiveProfile} onChange={(event) => setAdaptiveProfile(event.target.value as typeof adaptiveProfile)}><option value="gentle">Sanft</option><option value="balanced">Ausgewogen</option><option value="focused">Fokussiert</option></select></label>
            <div className="adaptive-assignment-composer-actions"><button type="button" className="secondary" onClick={() => setAdaptiveComposerOpen(false)}>Abbrechen</button><button type="button" className="primary" disabled={busy || !adaptiveLearnerId || !adaptiveTitle.trim()} onClick={() => createAdaptiveAssignment().catch((error) => setMessage(error.message))}>Adaptive Empfehlung speichern</button></div>
          </section>}
        </>}
        {message && <div className="alert info">{message}</div>}
      </section>
    );
  }

  const currentWord = exercise.run.words[exercise.index]!;
  const runMaterialBank = config.materialBanks.find(({ id }) => id === exercise.run.settings.materialBankId);
  const runItemSingular = runMaterialBank?.itemLabelSingular ?? "Wort";
  const runItemPlural = runMaterialBank?.itemLabelPlural ?? "Wörter";
  const wordVisible = exercise.phase === "showing" && isBlitzlesenWordVisible(exercise, realtime.serverNow());
  const presentationByWordId = new Map(exercise.presentations.map((entry) => [entry.wordId, entry]));
  const shownWords = newestFirst(exercise.run.words.flatMap((word) => {
    const presentation = presentationByWordId.get(word.id);
    return presentation && presentation.displayStartsAt <= realtime.serverNow() ? [{ word, presentation }] : [];
  }));
  const shownCount = shownWords.length;
  const correct = [...ratings.values()].filter((rating) => rating === "correct").length;
  const uncertain = [...ratings.values()].filter((rating) => rating === "uncertain").length;
  const incorrect = [...ratings.values()].filter((rating) => rating === "incorrect").length;
  const colorsAvailableForRun = runMaterialBank
    ? runMaterialBank.groups.some(({ id, syllableColors: available }) => exercise.run.settings.groupIds.includes(id) && available)
    : exercise.run.settings.syllableCounts.some((count) => config.syllableColorCounts.includes(count));
  const wordContent = exercise.liveSettings.syllableColors && colorsAvailableForRun
    ? <span>{(currentWord.syllables ?? blitzlesenSyllables(currentWord.text)).map((syllable, index) => <span className={`blitz-syllable color-${index % 2}`} key={`${syllable}-${index}`}>{syllable}</span>)}</span>
    : currentWord.text;

  return (
    <section className="blitzlesen-exercise">
      {canControl && exercise.phase !== "review" && exercise.phase !== "complete" && (
        <ExerciseMenu>
          <button className="secondary small" disabled={busy} onClick={() => backToSettings().catch((error) => setMessage(error.message))}>← Zurück zu den Einstellungen</button>
          <button className="secondary small" disabled={busy} onClick={() => remix().catch((error) => setMessage(error.message))}>↻ Neu mischen</button>
        </ExerciseMenu>
      )}
      <div className="exercise-progress">{runItemSingular} {shownCount} von {exercise.run.words.length}</div>
      {realtime.session?.participantRole === "FACILITATOR" && exercise.phase !== "review" && exercise.phase !== "complete" && <div className="blitz-fullscreen-actions">
        <button type="button" className="secondary small" aria-pressed={fullscreenMatches} disabled={busy || !syncControlReady}
          onClick={() => realtime.publishBlitzlesenFullscreen({ pageId, runId: exercise.runId, enabled: !fullscreenMatches })}>
          {fullscreenMatches ? "Vollbild beenden" : `Vollbild: ${labels.LEARNER.singular}`}
        </button>
      </div>}
      {exercise.phase === "complete" && <div className="prima-celebration" aria-hidden="true" />}
      <div ref={displayRef} className={`blitz-display learning-text${fullscreenActive ? " is-fullscreen" : ""}`}>
        {fullscreenActive && <>
          <button ref={closeFullscreenRef} type="button" className="blitz-fullscreen-close" aria-label="Vollbild schließen" title="Vollbild schließen" onClick={closeFullscreen}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
          </button>
          <span ref={measureRef} className="blitz-fullscreen-measure" aria-hidden="true">{wordContent}</span>
        </>}
        <span className="blitz-word" aria-live="polite">
          {wordVisible ? wordContent : exercise.phase === "complete" ? "✓ Erledigt" : ""}
        </span>
      </div>
      {canControl && (exercise.phase === "ready" || exercise.phase === "showing" || exercise.phase === "rating") && (
        <div className="blitz-live-settings" aria-label="Steuerung für die nächsten Wörter">
          <label>
            Anzeigedauer
            <div className="blitz-speed-control">
              <button type="button" className="ghost small speed-minus-large" disabled={busy} onClick={() => changeLiveSettings({ displayDurationMs: clampDuration(exercise.liveSettings.displayDurationMs - 1000) })}>−1000</button>
              <button type="button" className="ghost small speed-minus-small" disabled={busy} onClick={() => changeLiveSettings({ displayDurationMs: clampDuration(exercise.liveSettings.displayDurationMs - 100) })}>−100</button>
              <div className="blitz-speed-value">
                <input
                  aria-label="Anzeigedauer in Millisekunden"
                  type="number"
                  min={50}
                  max={9000}
                  step={50}
                  inputMode="numeric"
                  disabled={busy}
                  value={liveDisplayDurationDraft ?? exercise.liveSettings.displayDurationMs}
                  onChange={(event) => setLiveDisplayDurationDraft(editableInteger(event.target.value))}
                  onBlur={commitLiveDisplayDuration}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") event.currentTarget.blur();
                  }}
                />
                <span>ms</span>
              </div>
              <button type="button" className="ghost small speed-plus-small" disabled={busy} onClick={() => changeLiveSettings({ displayDurationMs: clampDuration(exercise.liveSettings.displayDurationMs + 100) })}>+100</button>
              <button type="button" className="ghost small speed-plus-large" disabled={busy} onClick={() => changeLiveSettings({ displayDurationMs: clampDuration(exercise.liveSettings.displayDurationMs + 1000) })}>+1000</button>
            </div>
            <small>Änderungen gelten ab dem nächsten eingeblendeten Wort.</small>
          </label>
          {colorsAvailableForRun && <label className="checkbox-label">
            <input type="checkbox" disabled={busy} checked={exercise.liveSettings.syllableColors} onChange={(event) => changeLiveSettings({ syllableColors: event.target.checked })} />
            Silben sofort einfärben
          </label>}
        </div>
      )}
      {canControl && exercise.phase === "ready" && <div className="blitz-start-actions"><button className="primary" disabled={busy || !syncControlReady} onClick={beginPresentation}>▶ Start</button></div>}
      {canControl && exercise.phase === "rating" && (
        <div className="blitz-rating-actions">
          <button className="secondary blitz-correct" disabled={busy || !syncControlReady} onClick={() => rate(currentWord.id, "correct", true).catch((error) => setMessage(error.message))}>✅ Richtig</button>
          <button className="secondary blitz-uncertain" disabled={busy || !syncControlReady} onClick={() => rate(currentWord.id, "uncertain", true).catch((error) => setMessage(error.message))}>🟡 Unsicher</button>
          <button className="secondary blitz-incorrect" disabled={busy || !syncControlReady} onClick={() => rate(currentWord.id, "incorrect", true).catch((error) => setMessage(error.message))}>❌ Falsch</button>
        </div>
      )}
      {showTherapistList && (
        <aside className="blitz-therapist-panel">
          <div className="section-title-row"><div><h3 className="learning-text">Angezeigte {runItemPlural} (Sync)</h3><small>{correct} richtig · {uncertain} unsicher · {incorrect} falsch</small></div><button className="secondary small" onClick={copyResult}>Liste kopieren</button></div>
          {copyMessage && <div className="alert info">{copyMessage}</div>}
          <div className="blitz-word-list">{shownWords.map(({ word, presentation }, index) => {
            const rating = ratings.get(word.id);
            return <div className={`blitz-word-row ${rating ?? "pending"}`} key={word.id}><span>{shownWords.length - index}.</span><strong>{word.text} <small>({presentation.displayDurationMs} ms)</small></strong><div>
              <button aria-label={`${word.text} richtig`} className={rating === "correct" ? "active correct" : ""} disabled={busy || !canControl || !syncControlReady || exercise.phase === "review" || exercise.phase === "complete"} onClick={() => rate(word.id, "correct", false).catch((error) => setMessage(error.message))}>✅</button>
              <button aria-label={`${word.text} unsicher`} className={rating === "uncertain" ? "active uncertain" : ""} disabled={busy || !canControl || !syncControlReady || exercise.phase === "review" || exercise.phase === "complete"} onClick={() => rate(word.id, "uncertain", false).catch((error) => setMessage(error.message))}>🟡</button>
              <button aria-label={`${word.text} falsch`} className={rating === "incorrect" ? "active incorrect" : ""} disabled={busy || !canControl || !syncControlReady || exercise.phase === "review" || exercise.phase === "complete"} onClick={() => rate(word.id, "incorrect", false).catch((error) => setMessage(error.message))}>❌</button>
            </div></div>;
          })}</div>
        </aside>
      )}
      {exercise.phase === "review" && <div className="reime-result"><strong>{correct} richtig · {uncertain} unsicher · {incorrect} falsch</strong><p>Der Durchlauf ist abgeschlossen.</p></div>}
      {canControl && exercise.phase === "review" && <CompletedRunActions onFinish={() => closeCompletedRun(false)} onRepeat={() => closeCompletedRun(true)} onError={setMessage} />}
      {exercise.phase === "review" && <CompletedRunHistory key={exercise.runId} runId={exercise.runId} />}
      {message && <div className="alert info">{message}</div>}
    </section>
  );
}
